import {
  Briefcase,
  TrendingUp,
  Building,
  Sparkles,
  DollarSign,
  Users,
} from 'lucide-react'
import { RolePageData } from '../types'

export const founderData: RolePageData = {
  id: 'founders',
  slug: 'founders',
  label: 'Founders & CEOs',
  icon: Briefcase,
  title: 'Does this make us look good and scale?',
  description:
    'Simplicity, strong brand signal, and one supplier that grows with you.',

  context:
    "You're not buying directly, but you approve budgets and think high-level about brand and culture.",

  jobToBeDone: 'Does this make us look good and scale?',

  dailyReality: [
    'Approving budgets for various initiatives',
    'Thinking about brand perception',
    'Building and maintaining company culture',
    'Evaluating cost vs value for investments',
  ],

  painPoints: [
    {
      title: 'Brand perception at stake',
      description:
        'Every touchpoint matters. Cheap or generic merch sends the wrong signal about your company.',
      icon: Building,
    },
    {
      title: 'Culture building challenges',
      description:
        'Remote teams need physical touchpoints to feel connected. Getting this right matters.',
      icon: Users,
    },
    {
      title: 'Cost vs value decisions',
      description:
        "Merch spend needs to make sense. It's an investment in brand and culture, not just an expense.",
      icon: DollarSign,
    },
    {
      title: 'Scalability concerns',
      description:
        'What works for 50 people needs to work for 500. Systems matter more than one-off wins.',
      icon: TrendingUp,
    },
  ],

  hiddenFears: [
    'We look cheap to employees and clients',
    'This won\'t scale with our growth',
    'We\'re overpaying for underwhelming results',
  ],

  esgAngle: {
    level: 'medium',
    description:
      'Medium sensitivity. Cares about brand alignment with values and not getting caught on the wrong side of sustainability expectations.',
  },

  whatTheyValue: ['Brand', 'Culture', 'Cost vs value', 'Scalability'],

  languageTheyUse: [
    'Brand signal',
    'Culture',
    'Scalable',
    'ROI',
    'Impression',
    'Values',
  ],

  convincers: [
    {
      quote: 'Strong brand signal',
      description:
        'Quality merch that makes your company look as good as it is. Premium products, perfect execution.',
    },
    {
      quote: 'One supplier that scales',
      description:
        'From 10 employees to 1,000. One relationship, one process, growing with you.',
    },
    {
      quote: 'Simplicity at every level',
      description:
        'Your team gets easy ordering. You get clear reporting. Everyone wins.',
    },
    {
      quote: 'Cost transparency',
      description:
        'Know exactly what you\'re spending and why. No surprises, no hidden fees.',
    },
  ],

  dealBreakers: [
    {
      title: 'Looks cheap or generic',
      description:
        'If the merch doesn\'t reflect your brand quality, it\'s doing more harm than good.',
    },
    {
      title: 'Won\'t scale',
      description:
        'Solutions that work for 20 people but break at 200 waste everyone\'s time.',
    },
    {
      title: 'Hidden complexity',
      description:
        'If it takes too much of your team\'s time, the cost isn\'t worth it.',
    },
  ],

  testimonial: {
    headline: 'Merch that actually matches our brand ambition',
    name: 'Alexander Berg',
    title: 'CEO, Momentum Ventures',
    image:
      'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop&crop=face',
  },

  faqs: [
    {
      question: 'How do you ensure brand quality?',
      answer:
        'We work with premium suppliers and provide pre-production samples. Every order gets quality checks. Your brand is safe with us.',
    },
    {
      question: 'Can this scale as we grow?',
      answer:
        'Absolutely. Our systems handle companies from startup to enterprise. As you grow, pricing improves and processes stay simple.',
    },
    {
      question: 'How do you handle multiple teams ordering?',
      answer:
        'Centralized control with distributed ordering. Teams can order within budgets and guidelines you set. You get visibility without bottlenecks.',
    },
    {
      question: 'What\'s the typical ROI on branded merch?',
      answer:
        'It\'s hard to quantify directly, but companies consistently report improved employee satisfaction, better client relationships, and stronger brand recognition.',
    },
  ],

  cta: {
    primary: 'See how merch scales with you',
    secondary: 'Get a brand-quality proposal',
    promptTemplate:
      "Hi! I'm looking for a merch solution that scales with our company growth. I want premium quality that reflects our brand well. Can you show me how this works?",
  },
}
